/*
 * Copyright © 2022 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {DestroyRef, Directive, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LogFactory} from '@dv/shared/code';
import type {Observable} from 'rxjs';
import {BehaviorSubject, combineLatest, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

const LOG = LogFactory.createLog('auth.directive');

@Directive()
export abstract class AbstractAuthzDirective implements OnInit {

    private destroyRef = inject(DestroyRef);

    private readonly granted$ = new BehaviorSubject<Observable<boolean>>(of(false));
    private readonly condition$ = new BehaviorSubject<boolean>(true);

    public ngOnInit(): void {
        combineLatest([
            this.granted$.pipe(switchMap(granted$ => granted$)), this.condition$,
        ]).pipe(
            map(([granted, condition]) => granted && condition),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: granted => this.refresh(granted),
            error: (error: unknown) => LOG.error(error),
        });
    }

    protected changeGranted(granted$: Observable<boolean>): void {
        this.granted$.next(granted$);
    }

    protected changeCondition(condition: boolean): void {
        this.condition$.next(condition);
    }

    private refresh(granted: boolean): void {
        LOG.trace('refresh, granted: ', granted);
        this.setDisabled(!granted);
    }

    protected abstract setDisabled(disabled: boolean): void;
}
